import { useState } from "react";
import type { RefObject } from "react";
import { useAtomValue } from "jotai";
import { ConfirmDialog } from "@common/ConfirmDialog";
import { ComponentColor } from "@common/types";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { isProcessingCategoriesAtom } from "@/store/categoryAtoms";
import type { CategoryResponse } from "@models/category";
import { deleteCategoryErrorMessage } from "../_helpers/deleteCategoryCopy";

interface DeleteCategoryDialogProps {
    category: Pick<CategoryResponse, "id" | "name">;
    // Called on No - Keep Category, Escape, the backdrop, and after a delete.
    onClose: () => void;
    // Focused on close when the opener is gone: after a delete, the category's
    // tree row no longer exists.
    returnFocusRef: RefObject<HTMLElement | null>;
}

// I4 (PO ruling, 2026-10-27): a category is deleted only when nothing uses it.
// The shared ConfirmDialog chassis (focus trap, Escape, backdrop, scroll lock,
// processing lockdown, the safe back-out first and focused, #452). A refusal
// or failure keeps the dialog open on its own copy; only a delete closes it.
export const DeleteCategoryDialog = ({
    category,
    onClose,
    returnFocusRef,
}: DeleteCategoryDialogProps) => {
    const { deleteCategory } = useCategoryApi();
    const isProcessing = useAtomValue(isProcessingCategoriesAtom);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleDelete = async () => {
        // A retry must not show the last attempt's outcome while it is out.
        setErrorMessage(null);

        try {
            await deleteCategory(category.id);
            onClose();
        } catch (error) {
            setErrorMessage(deleteCategoryErrorMessage(category.name, error));
        }
    };

    return (
        <ConfirmDialog
            title="Delete Category?"
            body={
                <p>
                    <strong>{category.name}</strong> will be deleted. This
                    can&apos;t be undone.
                </p>
            }
            confirmLabel="Yes - Delete Category"
            confirmColor={ComponentColor.Error}
            dismissLabel="No - Keep Category"
            isProcessing={isProcessing}
            errorMessage={errorMessage}
            returnFocusRef={returnFocusRef}
            onConfirm={handleDelete}
            onClose={onClose}
        />
    );
};
